import i18n from '../i18n/config';
import { useTranslation } from 'react-i18next';
// frontend/src/pages/ProductDetailPage.jsx - DISEÑO MEJORADO
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Container,
    Grid,
    Box,
    Typography,
    Button,
    Card,
    CardMedia,
    Chip,
    Divider,
    TextField,
    IconButton,
    Alert,
    Breadcrumbs,
    Link,
    Tabs,
    Tab,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableRow,
    Paper,
    CircularProgress,
    Skeleton,
    Stack,
    alpha,
    useTheme,
    Menu,
    MenuItem,
    ListItemIcon,
    ListItemText,
} from '@mui/material';
import {
    Add as AddIcon,
    Remove as RemoveIcon,
    ShoppingCart as CartIcon,

    Share as ShareIcon,
    ArrowBack as BackIcon,
    LocalShipping as ShippingIcon,
    Security as SecurityIcon,
    VerifiedUser as VerifiedIcon,
    CheckCircle as CheckIcon,
    NavigateNext as NavigateNextIcon,
    ContentCopy as ContentCopyIcon,
    WhatsApp as WhatsAppIcon,
    Email as EmailIcon,
    Facebook as FacebookIcon,
    Telegram as TelegramIcon,
    Twitter as TwitterIcon,
} from '@mui/icons-material';

import { ecommerceApi, cartHelpers, productHelpers } from '../services/ecommerceApi';
import { useCart } from '../hooks/useCart';
import { useNotification } from '../components/common/GlobalNotifications';
import ProductCard from '../components/store/ProductCard';

const ProductDetailPage = () => {
  const { t } = useTranslation();
    const { productId } = useParams();
    const navigate = useNavigate();
    const theme = useTheme();
    const { addToCart, isInCart, loadCart, getCartItemQuantity, openCartDrawer } = useCart();
    const { showSuccess, showError } = useNotification();

    const [product, setProduct] = useState(null);
    const [loading, setLoading] = useState(true);
    const [quantity, setQuantity] = useState(1);
    const [selectedImage, setSelectedImage] = useState(0);
    const [activeTab, setActiveTab] = useState(0);
    const [relatedProducts, setRelatedProducts] = useState([]);
    const [addingToCart, setAddingToCart] = useState(false);
    const [shareAnchorEl, setShareAnchorEl] = useState(null);

    useEffect(() => {
        if (!productId || productId === 'undefined' || productId === 'null') {
            showError(t('hardcoded.pages_productdetailpage.id_de_producto_invalido'));
            navigate('/store');
            return;
        }
        loadProduct();
    }, [productId]);

    const loadProduct = async () => {
        setLoading(true);
        try {
            const response = await ecommerceApi.products.getById(productId);
            setProduct(response.data);

            if (response.data.category_id) {
                loadRelatedProducts(response.data.category_id, productId);
            }
        } catch (error) {
            console.error(t('hardcoded.pages_productdetailpage.error_loading_product'), error);
            showError(t('hardcoded.pages_productdetailpage.error_al_cargar_el_producto'));
            navigate('/store');
        } finally {
            setLoading(false);
        }
    };

    const handleShare = async () => {
        const shareUrl = window.location.href;
        try {
            if (navigator.share) {
                await navigator.share({
                    title: product?.name || t('hardcoded.pages_productdetailpage.producto_arsenal_tpms'),
                    text: product?.short_description || t('hardcoded.pages_productdetailpage.revisa_este_producto'),
                    url: shareUrl,
                });
                return;
            }
            await navigator.clipboard.writeText(shareUrl);
            showSuccess(t('hardcoded.pages_kitdetailpage.enlace_copiado_al_portapapeles'));
        } catch (err) {
            showError(t('hardcoded.pages_kitdetailpage.no_se_pudo_compartir_el'));
        }
    };

    const handleOpenShareMenu = (event) => {
        setShareAnchorEl(event.currentTarget);
    };

    const handleCloseShareMenu = () => {
        setShareAnchorEl(null);
    };

    const openShareLink = async (type) => {
        const url = window.location.href;
        const title = encodeURIComponent(product?.name || t('hardcoded.pages_productdetailpage.producto_arsenal_tpms'));
        const encodedUrl = encodeURIComponent(url);

        if (type === 'copy') {
            await handleShare();
            handleCloseShareMenu();
            return;
        }

        const links = {
            whatsapp: `https://wa.me/?text=${title}%20${encodedUrl}`,
            email: `mailto:?subject=${title}&body=${encodedUrl}`,
            facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
            x: `https://twitter.com/intent/tweet?text=${title}&url=${encodedUrl}`,
            telegram: `https://t.me/share/url?url=${encodedUrl}&text=${title}`,
        };

        const target = links[type];
        if (target) {
            window.open(target, t('hardcoded.components_operators_operatorcontactmenu._blank'), t('hardcoded.components_operators_operatorcontactmenu.noopenernoreferrer'));
        }
        handleCloseShareMenu();
    };

    const loadRelatedProducts = async (categoryId, excludeId) => {
        try {
            const response = await ecommerceApi.products.getAll({
                category_id: categoryId,
                per_page: 4
            });

            const filtered = response.data.items.filter(p => p.id !== excludeId);
            setRelatedProducts(filtered.slice(0, 4));
        } catch (error) {
            console.error('Error loading related products:', error);
        }
    };

    const handleAddToCart = async () => {
        if (!product.is_in_stock) {
            showError(t('hardcoded.components_store_productcard.producto_no_disponible'));
            return;
        }

        setAddingToCart(true);
        try {
            await addToCart({
                product_id: product.id,
                quantity: quantity
            });

            showSuccess(t('hardcoded.pages_productdetailpage.quantity_name_agregadoquantity1s_al_carrito', { quantity: quantity, name: product.name, quantity1s: quantity > 1 ? 's' : '' }));
            openCartDrawer();
            setQuantity(1);
        } catch (error) {
            showError(error.message || t('hardcoded.components_store_productcard.error_al_agregar_al_carrito'));
        } finally {
            setAddingToCart(false);
        }
    };

    const handleQuantityChange = (newQuantity) => {
        if (newQuantity < 1) return;
        if (product.track_inventory && newQuantity > product.stock_quantity && !product.allow_backorder) {
            showError(`Solo ${product.stock_quantity} disponibles`);
            return;
        }
        setQuantity(newQuantity);
    };

    if (loading) {
        return (
            <Container maxWidth="lg" sx={{ py: 4 }}>
                <Grid container spacing={4}>
                    <Grid item xs={12} md={6}>
                        <Skeleton variant="rectangular" height={500} sx={{ borderRadius: 2 }} />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Skeleton variant="text" height={50} />
                        <Skeleton variant="text" height={30} width="60%" />
                        <Skeleton variant="rectangular" height={100} sx={{ my: 2 }} />
                        <Skeleton variant="rectangular" height={60} />
                    </Grid>
                </Grid>
            </Container>
        );
    }

    if (!product) {
        return (
            <Container maxWidth="lg" sx={{ py: 4 }}>
                <Alert severity="error">{t('hardcoded.pages_productdetailpage.producto_no_encontrado')}</Alert>
            </Container>
        );
    }

    const stockStatus = productHelpers.getStockStatus(product);
    const isOnSale = productHelpers.isOnSale(product);
    const discountPercentage = productHelpers.getDiscountPercentage(product);
    const specifications = productHelpers.formatSpecifications(product.specifications);
    const inCart = isInCart(product.id);
    const cartQuantity = getCartItemQuantity(product.id);


    const allImages = product.images && product.images.length > 0 ?
        product.images :
        [product.main_image_url || '/images/placeholder-product.jpg'];

    return (
        <Box sx={{ bgcolor: 'background.default', minHeight: '100vh', py: 4 }}>
            <Container maxWidth="lg">
                {/* Breadcrumbs */}
                <Breadcrumbs
                    separator={<NavigateNextIcon fontSize="small" />}
                    sx={{ mb: 3 }}
                >
                    <Link
                        color="inherit"
                        href="/store"
                        onClick={(e) => {
                            e.preventDefault();
                            navigate('/store');
                        }}
                        sx={{
                            cursor: 'pointer',
                            '&:hover': { textDecoration: 'underline' }
                        }}
                    >
                        {i18n.t('hardcoded.components_common_sidebar.tienda')}</Link>
                    {product.category && (
                        <Link
                            color="inherit"
                            href={`/store?category=${product.category?.id}`}
                            onClick={(e) => {
                                e.preventDefault();
                                navigate(`/store?category=${product.category?.id}`);
                            }}
                            sx={{
                                cursor: 'pointer',
                                '&:hover': { textDecoration: 'underline' }
                            }}
                        >
                            {product.category?.name}
                        </Link>
                    )}
                    <Typography color="text.primary">{product.name}</Typography>
                </Breadcrumbs>

                {/* Back Button */}
                <Button
                    startIcon={<BackIcon />}
                    onClick={() => navigate(-1)}
                    sx={{ mb: 3 }}
                >
                    {i18n.t('hardcoded.components_operators_operatorquickcreateform.volver')}</Button>

                <Grid container spacing={4}>
                    {/* Product Images */}
                    <Grid item xs={12} md={6}>
                        <Box sx={{ position: 'sticky', top: 20 }}>
                            {/* Main Image */}
                            <Paper
                                elevation={2}
                                sx={{
                                    mb: 2,
                                    overflow: 'hidden',
                                    borderRadius: 2,
                                    position: 'relative'
                                }}
                            >
                                <CardMedia
                                    component="img"
                                    height="500"
                                    image={allImages[selectedImage]}
                                    alt={product.name}
                                    sx={{
                                        objectFit: 'cover',
                                        backgroundColor: 'grey.100'
                                    }}
                                />

                                {/* Badges */}
                                {isOnSale && (
                                    <Chip
                                        label={`-${discountPercentage}%`}
                                        color="error"
                                        sx={{
                                            position: 'absolute',
                                            top: 16,
                                            left: 16,
                                            fontWeight: 700,
                                            fontSize: '1rem',
                                            px: 1
                                        }}
                                    />
                                )}
                                {product.is_featured && (
                                    <Chip
                                        label={t('hardcoded.components_store_productcard.destacado')}
                                        color="primary"
                                        sx={{
                                            position: 'absolute',
                                            top: 16,
                                            right: 16,
                                            fontWeight: 600
                                        }}
                                    />
                                )}
                                <IconButton
                                    size="small"
                                    onClick={handleOpenShareMenu}
                                    sx={{
                                        position: 'absolute',
                                        top: 16,
                                        right: 16,
                                        bgcolor: 'rgba(255,255,255,0.92)',
                                        border: '1px solid',
                                        borderColor: 'divider',
                                        '&:hover': { bgcolor: 'white' },
                                    }}
                                >
                                    <ShareIcon fontSize="small" />
                                </IconButton>
                                <Menu anchorEl={shareAnchorEl} open={Boolean(shareAnchorEl)} onClose={handleCloseShareMenu}>
                                    <MenuItem onClick={() => openShareLink('copy')}>
                                        <ListItemIcon><ContentCopyIcon fontSize="small" /></ListItemIcon>
                                        <ListItemText>{i18n.t('inviteUser.success.copyLink')}</ListItemText>
                                    </MenuItem>
                                    <MenuItem onClick={() => openShareLink('whatsapp')}>
                                        <ListItemIcon><WhatsAppIcon fontSize="small" /></ListItemIcon>
                                        <ListItemText>{t('pages_productdetailpage.share_whatsapp')}</ListItemText>
                                    </MenuItem>
                                    <MenuItem onClick={() => openShareLink('email')}>
                                        <ListItemIcon><EmailIcon fontSize="small" /></ListItemIcon>
                                        <ListItemText>{i18n.t('hardcoded.components_settings_companynetworkstab.correo')}</ListItemText>
                                    </MenuItem>
                                    <MenuItem onClick={() => openShareLink('facebook')}>
                                        <ListItemIcon><FacebookIcon fontSize="small" /></ListItemIcon>
<ListItemText>{t('pages_productdetailpage.share_facebook')}</ListItemText>
                                    </MenuItem>
                                    <MenuItem onClick={() => openShareLink('x')}>
                                        <ListItemText>{t('pages_productdetailpage.share_x')}</ListItemText>
                                    </MenuItem>
                                    <MenuItem onClick={() => openShareLink('telegram')}>
                                        <ListItemIcon><TelegramIcon fontSize="small" /></ListItemIcon>
                                        <ListItemText>{i18n.t('hardcoded.pages_kitdetailpage.telegram')}</ListItemText>
                                    </MenuItem>
                                </Menu>
                            </Paper>

                            {/* Thumbnail Images */}
                            {allImages.length > 1 && (
                                <Stack direction="row" spacing={1} sx={{ overflowX: 'auto', pb: 1 }}>
                                    {allImages.map((image, index) => (
                                        <Paper
                                            key={index}
                                            elevation={selectedImage === index ? 4 : 1}
                                            sx={{
                                                minWidth: 80,
                                                cursor: 'pointer',
                                                border: '2px solid',
                                                borderColor: selectedImage === index ? 'primary.main' : 'transparent',
                                                borderRadius: 1,
                                                overflow: 'hidden',
                                                transition: 'all 0.2s ease',
                                                '&:hover': {
                                                    borderColor: 'primary.light',
                                                    transform: 'scale(1.05)'
                                                }
                                            }}
                                            onClick={() => setSelectedImage(index)}
                                        >
                                            <CardMedia
                                                component="img"
                                                height="80"
                                                image={image}
                                                alt={`${product.name} ${index + 1}`}
                                                sx={{ objectFit: 'cover' }}
                                            />
                                        </Paper>
                                    ))}
                                </Stack>
                            )}
                        </Box>
                    </Grid>

                    {/* Product Info */}
                    <Grid item xs={12} md={6}>
                        <Box>
                            {/* Product Title */}
                            <Typography variant="h3" fontWeight={700} gutterBottom>
                                {product.name}
                            </Typography>

                            {/* SKU & Stock Status */}
                            <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 3 }}>
                                <Typography variant="body2" color="text.secondary">
                                    SKU: <strong>{product.sku}</strong>
                                </Typography>
                                <Chip
                                    label={stockStatus.message}
                                    color={stockStatus.status === 'in_stock' ? 'success' :
                                        stockStatus.status === 'low_stock' ? 'warning' : 'error'}
                                    icon={stockStatus.status === 'in_stock' ? <CheckIcon /> : undefined}
                                    size="small"
                                />
                            </Stack>

                            {/* Pricing */}
                            <Paper
                                elevation={0}
                                sx={{
                                    p: 3,
                                    mb: 3,
                                    bgcolor: alpha(theme.palette.primary.main, 0.05),
                                    border: '2px solid',
                                    borderColor: alpha(theme.palette.primary.main, 0.1),
                                    borderRadius: 2
                                }}
                            >
                                <Stack direction="row" alignItems="baseline" spacing={2}>
                                    <Typography variant="h3" color="primary" fontWeight={700}>
                                        {cartHelpers.formatCurrency(product.price)}
                                    </Typography>
                                    {isOnSale && (
                                        <>
                                            <Typography
                                                variant="h5"
                                                color="text.secondary"
                                                sx={{ textDecoration: 'line-through' }}
                                            >
                                                {cartHelpers.formatCurrency(product.compare_price)}
                                            </Typography>
                                            <Chip
                                                label={t('hardcoded.pages_kitdetailpage.ahorras_carthelpersform', { cartHelpersform: cartHelpers.formatCurrency(product.compare_price - product.price) })}
                                                color="success"
                                                size="small"
                                            />
                                        </>
                                    )}
                                </Stack>
                            </Paper>

                            {/* Short Description */}
                            {product.short_description && (
                                <Typography variant="body1" color="text.secondary" sx={{ mb: 3, lineHeight: 1.8 }}>
                                    {product.short_description}
                                </Typography>
                            )}

                            {/* Quantity Selector */}
                            {product.is_in_stock && (
                                <Box sx={{ mb: 3 }}>
                                    <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                                        {t('hardcoded.components_store_cartitemline.cantidad')}</Typography>
                                    <Stack direction="row" spacing={2} alignItems="center">
                                        <Paper
                                            elevation={1}
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                border: '1px solid',
                                                borderColor: 'divider',
                                                borderRadius: 2
                                            }}
                                        >
                                            <IconButton
                                                onClick={() => handleQuantityChange(quantity - 1)}
                                                disabled={quantity <= 1}
                                                sx={{ borderRadius: '8px 0 0 8px' }}
                                            >
                                                <RemoveIcon />
                                            </IconButton>
                                            <TextField
                                                value={quantity}
                                                onChange={(e) => handleQuantityChange(parseInt(e.target.value) || 1)}
                                                inputProps={{
                                                    min: 1,
                                                    max: product.track_inventory ? product.stock_quantity : undefined,
                                                    style: { textAlign: 'center', width: '60px', fontWeight: 600 }
                                                }}
                                                variant="standard"
                                                sx={{
                                                    '& .MuiInput-underline:before': { display: 'none' },
                                                    '& .MuiInput-underline:after': { display: 'none' }
                                                }}
                                            />
                                            <IconButton
                                                onClick={() => handleQuantityChange(quantity + 1)}
                                                disabled={product.track_inventory && quantity >= product.stock_quantity && !product.allow_backorder}
                                                sx={{ borderRadius: '0 8px 8px 0' }}
                                            >
                                                <AddIcon />
                                            </IconButton>
                                        </Paper>
                                        {product.track_inventory && (
                                            <Typography variant="body2" color="text.secondary">
                                                {product.stock_quantity} disponibles
                                            </Typography>
                                        )}
                                    </Stack>
                                </Box>
                            )}

                            {/* Action Buttons */}
                            <Stack direction="row" spacing={2} sx={{ mb: 4 }}>
                                <Button
                                    variant="contained"
                                    size="large"
                                    startIcon={addingToCart ? <CircularProgress size={20} color="inherit" /> : <CartIcon />}
                                    onClick={handleAddToCart}
                                    disabled={!product.is_in_stock || addingToCart}
                                    sx={{
                                        flex: 1,
                                        py: 2,
                                        fontSize: '1.1rem',
                                        fontWeight: 700,
                                        textTransform: 'none',
                                        boxShadow: 3,
                                        '&:hover': {
                                            boxShadow: 6
                                        }
                                    }}
                                >
                                    {addingToCart ? t('hardcoded.components_store_productcard.agregando') :
                                        inCart ? t('hardcoded.pages_kitdetailpage.agregar_mas_cartquantity_en_carrito', { cartQuantity: cartQuantity }) :
                                            !product.is_in_stock ? t('hardcoded.components_store_productcard.no_disponible') :
                                                t('hardcoded.components_store_productcard.agregar_al_carrito')}
                                </Button>
                            </Stack>

                            {/* Trust Badges */}
                            <Paper
                                elevation={0}
                                sx={{
                                    p: 2,
                                    bgcolor: 'grey.50',
                                    borderRadius: 2,
                                    border: '1px solid',
                                    borderColor: 'divider'
                                }}
                            >
                                <Stack spacing={2}>
                                    <Stack direction="row" alignItems="center" spacing={2}>
                                        <VerifiedIcon color="primary" />
                                        <Box>
                                            <Typography variant="body2" fontWeight={600}>
                                                {t('hardcoded.pages_kitdetailpage.garantia_de_calidad_arsenal')}</Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {i18n.t('hardcoded.pages_kitdetailpage.productos_certificados_y_originales')}</Typography>
                                        </Box>
                                    </Stack>
                                    <Divider />
                                    <Stack direction="row" alignItems="center" spacing={2}>
                                        <ShippingIcon color="primary" />
                                        <Box>
                                            <Typography variant="body2" fontWeight={600}>
                                                {t('hardcoded.pages_kitdetailpage.envio_rapido')}</Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {t('hardcoded.pages_productdetailpage.entrega_en_3-5_dias_habiles')}</Typography>
                                        </Box>
                                    </Stack>
                                    <Divider />
                                    <Stack direction="row" alignItems="center" spacing={2}>
                                        <SecurityIcon color="primary" />
                                        <Box>
                                            <Typography variant="body2" fontWeight={600}>
                                                {i18n.t('hardcoded.pages_kitdetailpage.compra_segura')}</Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {t('hardcoded.pages_kitdetailpage.proteccion_en_cada_transaccion')}</Typography>
                                        </Box>
                                    </Stack>
                                </Stack>
                            </Paper>
                        </Box>
                    </Grid>
                </Grid>

                {/* Product Details Tabs */}
                <Box sx={{ mt: 6 }}>
                    <Paper elevation={2} sx={{ borderRadius: 2, overflow: 'hidden' }}>
                        <Tabs
                            value={activeTab}
                            onChange={(e, newValue) => setActiveTab(newValue)}
                            sx={{
                                bgcolor: alpha(theme.palette.primary.main, 0.05),
                                borderBottom: 1,
                                borderColor: 'divider'
                            }}
                        >
                            <Tab label={t('audit.table.description')} sx={{ fontWeight: 600, textTransform: 'none', fontSize: '1rem' }} />
                            {specifications.length > 0 && (
                                <Tab label={t('hardcoded.pages_productdetailpage.especificaciones')} sx={{ fontWeight: 600, textTransform: 'none', fontSize: '1rem' }} />
                            )}
                        </Tabs>

                        <Box sx={{ p: 4 }}>
                            {/* Description Tab */}
                            {activeTab === 0 && (
                                <Box>
                                    <Typography variant="h5" fontWeight={600} gutterBottom>
                                        {t('hardcoded.pages_productdetailpage.descripcion_del_producto')}</Typography>
                                    <Typography variant="body1" sx={{ whiteSpace: 'pre-line', lineHeight: 1.8, color: 'text.secondary' }}>
                                        {product.description || product.short_description || t('hardcoded.pages_productdetailpage.no_hay_descripcion_disponible')}
                                    </Typography>
                                </Box>
                            )}

                            {/* Specifications Tab */}
                            {activeTab === 1 && specifications.length > 0 && (
                                <Box>
                                    <Typography variant="h5" fontWeight={600} gutterBottom sx={{ mb: 3 }}>
                                        {t('product.specifications')}</Typography>
                                    <TableContainer>
                                        <Table>
                                            <TableBody>
                                                {specifications.map((spec, index) => (
                                                    <TableRow
                                                        key={spec.label}
                                                        sx={{
                                                            '&:nth-of-type(odd)': {
                                                                bgcolor: alpha(theme.palette.primary.main, 0.02)
                                                            }
                                                        }}
                                                    >
                                                        <TableCell sx={{ fontWeight: 600, width: '35%', borderBottom: index === specifications.length - 1 ? 'none' : undefined }}>
                                                            {spec.label}
                                                        </TableCell>
                                                        <TableCell sx={{ borderBottom: index === specifications.length - 1 ? 'none' : undefined }}>
                                                            {spec.value}
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </Box>
                            )}
                        </Box>
                    </Paper>
                </Box>

                {/* Related Products */}
                {relatedProducts.length > 0 && (
                    <Box sx={{ mt: 6 }}>
                        <Typography variant="h4" fontWeight={700} gutterBottom>
                            {i18n.t('hardcoded.pages_productdetailpage.productos_relacionados')}</Typography>
                        <Grid container spacing={3}>
                            {relatedProducts.map((relatedProduct) => (
                                <Grid item xs={12} sm={6} md={3} key={relatedProduct.id}>
                                    <ProductCard
                                        product={relatedProduct}
                                        onProductClick={(product) => {
                                            navigate(`/store/product/${product.id}`);
                                            window.scrollTo({ top: 0, behavior: 'smooth' });
                                        }}
                                        compact={true}
                                    />
                                </Grid>
                            ))}
                        </Grid>
                    </Box>
                )}
            </Container>
        </Box>
    );
};

export default ProductDetailPage;